import React from 'react';
import {
  ShieldCheck, Briefcase, Users, CalendarClock, ClipboardList,
  Building2, ListChecks, BellRing, KeyRound,
  FileSpreadsheet, UserCog, Award, SlidersHorizontal, BookOpen,
  FileSignature, Archive, ClipboardCheck, ScrollText, AlarmClock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { isNotLivePath, NotLiveBadge } from '../components/shared/NotLive';
import HubCard, { HubCardGrid, HubSectionHeader } from '../components/shared/HubCard';

// Management hub — card index of every management page, grouped by what the
// page does: master data, business records, operations, system. Cards are
// shown only when the user holds the matching permission.
// Section headers and cards come from the shared Hub kit, so this page and the
// Workflow hub always look the same (components/shared/HubCard.js); only the
// icon colour differs — one colour per section, so the groups stay apart at a
// glance.
const SECTIONS = [
  {
    title: 'Master Data',
    description: 'Reference data used across the forms.',
    iconClass: 'bg-slate-100 text-slate-600',
    cards: [
      {
        name: 'Clients & Entities',
        href: '/settings/clients',
        permission: 'settings:clients:edit',
        icon: Building2,
        description: 'Client groups and entities.',
      },
      {
        name: 'Dropdown Lists',
        href: '/settings/lists',
        permission: 'settings:lists:edit',
        icon: ListChecks,
        description: 'Dropdown options used across the forms.',
      },
      {
        name: 'Staff',
        href: '/qm/staff',
        permission: 'qm:staff:edit',
        icon: Users,
        description: 'Staff master records.',
      },
    ],
  },
  {
    title: 'Engagements Records',
    description: 'Registered engagements and the records approved against them.',
    iconClass: 'bg-blue-50 text-blue-600',
    cards: [
      {
        name: 'Engagements',
        href: '/qm/engagements',
        permission: 'engagements:edit',
        icon: Briefcase,
        description: 'All registered engagements.',
      },
      {
        name: 'QC6 / QC7 Records',
        href: '/qm/qc-records',
        permission: 'qm:qc_records:edit',
        icon: ShieldCheck,
        description: 'Approved QC6 / QC7 forms.',
      },
      {
        // 核准產生的正式記錄。與 QC6/QC7 Records 並列:
        // 一樣是「workflow 送申請單、核准產生資料」的產物,一樣可以在這裡人工
        // 補建——建立與編輯都會回寫 engagement 主檔。
        //
        // CaseWare & Charge Code 沒有自己的管理卡：它沒有
        // 自己的欄位,兩欄都是 engagement 主檔的,Engagements 頁改得到。核准仍
        // 照樣寫 cwcc_records 存證。
        name: 'Signed Financial Statements Records',
        href: '/qm/records/fs',
        permission: 'engagements:edit',
        icon: FileSignature,
        description: 'Signed financial statements on file.',
      },
      {
        name: 'QC35 Form Records',
        href: '/qm/records/qc35',
        permission: 'engagements:edit',
        icon: Archive,
        description: 'QC35 Form file archival records.',
      },
      {
        name: 'Monthly Records',
        href: '/qm/monthly-records',
        permission: 'qm:monthly:edit',
        icon: CalendarClock,
        description: 'Monthly timesheet and chargeable hours records.',
      },
      {
        name: 'Registers',
        href: '/qm/registers',
        permission: 'qm:registers:edit',
        icon: ClipboardList,
        description: 'Complaints, Unlocking, EQR Lapses, Cyber Security and A&C Lapses.',
      },
    ],
  },
  {
    title: 'Screening',
    description: 'QM background check records.',
    iconClass: 'bg-teal-50 text-teal-600',
    cards: [
      {
        name: 'Screening Records',
        href: '/qm/screening-records',
        permission: 'qm:screening_records:edit',
        icon: ClipboardCheck,
        description: 'Completed QM background checks.',
      },
    ],
  },
  {
    title: 'L&D Records',
    description: 'Training, quizzes and staff development.',
    iconClass: 'bg-emerald-50 text-emerald-600',
    cards: [
      {
        name: 'Training Records',
        href: '/ld/records',
        permission: 'ld:training:edit',
        icon: CalendarClock,
        description: 'Training sessions, attendance and External CPD.',
      },
      {
        name: 'Quiz Bank',
        href: '/ld/quizzes',
        permission: 'ld:quiz:edit',
        icon: ClipboardCheck,
        description: 'Post-course quiz question papers.',
      },
      {
        name: 'Training Follow-up Reminder',
        href: '/ld/reminders',
        permission: 'ld:reminder:edit',
        icon: AlarmClock,
        description: 'Reminders for outstanding feedback and quizzes.',
      },
      {
        name: 'Staff Development',
        href: '/ld/records/development',
        permission: 'ld:development:edit',
        icon: Award,
        description: 'Qualifications and training agreements.',
      },
      {
        name: 'L&D Setup',
        href: '/ld/setup',
        permission: 'ld:setup:edit',
        icon: SlidersHorizontal,
        description: 'Trainers, categories, venues and training targets.',
      },
    ],
  },
  {
    title: 'Operations',
    description: 'Imports, checks and reminder emails.',
    iconClass: 'bg-amber-50 text-amber-600',
    cards: [
      {
        name: 'Adaptive Cookie',
        href: '/qm/adaptive-cookie',
        permission: 'qm:timesheet_ops:edit',
        icon: KeyRound,
        description: 'Adaptive Pay session for Chargeable Hours.',
      },
      {
        name: 'Chargeable Hours Import',
        href: '/qm/chargeable-import',
        permission: 'qm:timesheet_ops:edit',
        icon: FileSpreadsheet,
        description: 'Import timesheets from Adaptive.',
      },
      {
        name: 'Timesheet Check',
        href: '/qm/timesheet-check',
        permission: 'qm:timesheet_ops:edit',
        icon: ListChecks,
        description: 'Incomplete timesheet reminders.',
      },
      {
        name: 'Overdue Rules & Reminder',
        href: '/settings/overdue',
        permission: 'settings:overdue:edit',
        icon: BellRing,
        description: 'Overdue criteria and reminder emails.',
      },
    ],
  },
  {
    title: 'System',
    description: 'Accounts, permissions and system settings.',
    iconClass: 'bg-purple-50 text-purple-600',
    cards: [
      {
        name: 'Accounts',
        href: '/settings/accounts',
        permission: 'admin:users',
        icon: UserCog,
        description: 'Login accounts, roles and per-user permissions.',
      },
      {
        name: 'Roles & Default Permissions',
        href: '/settings/roles',
        permission: 'admin:users',
        icon: ShieldCheck,
        description: 'Roles and their default permissions.',
      },
      {
        name: 'Workflow Notifications',
        href: '/settings/notifications',
        permission: 'settings:notifications:edit',
        icon: BellRing,
        description: 'Workflow email notifications.',
      },
      {
        name: 'Permissions',
        href: '/settings/permission-reference',
        permission: 'admin:users',
        icon: BookOpen,
        description: 'Every permission and who holds it.',
      },
      {
        name: 'Admin Settings',
        href: '/settings/admin',
        permission: 'admin:settings',
        icon: ScrollText,
        description: 'Activity log, legacy import switches and security reset.',
      },
    ],
  },
];

const ManageData = () => {
  const { hasPermission } = useAuth();

  const visibleSections = SECTIONS
    .map(section => ({
      ...section,
      cards: section.cards.filter(card => hasPermission(card.permission)),
    }))
    .filter(section => section.cards.length > 0);

  if (visibleSections.length === 0) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800">Editor access required</h2>
          <p className="text-sm text-red-600 mt-1">You need data editing rights to access this area.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Management</h1>
        <p className="text-sm text-slate-500 mt-1">Master data, records, operations and system administration.</p>
      </div>

      <div className="space-y-8">
        {visibleSections.map(section => (
          <section key={section.title}>
            <HubSectionHeader title={section.title} description={section.description} />
            <HubCardGrid>
              {section.cards.map(card => {
                // 尚未啟動的功能：卡片打灰並標上 Not live，但仍然可點進去預覽。
                const notLive = isNotLivePath(card.href);
                return (
                  <HubCard
                    key={card.href + card.name}
                    to={card.href}
                    icon={card.icon}
                    name={card.name}
                    description={card.description}
                    iconClass={section.iconClass}
                    badge={notLive ? <NotLiveBadge /> : null}
                    muted={notLive}
                  />
                );
              })}
            </HubCardGrid>
          </section>
        ))}
      </div>
    </div>
  );
};

export default ManageData;
